import { Button, Drawer, Form, Input, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import { RequestEditInventoryControlRegister } from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import React, { useEffect } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { DailyStockControlData } from "./StockControlColumns";

interface InventoryControlCauseInconsistencyFormProps {
  isOpen: boolean;
  onClose: () => void;
  record: DailyStockControlData | undefined;
  allowEdit: boolean;
  onSubmit: (data: RequestEditInventoryControlRegister) => void;
  loading?: boolean;
}

export const InventoryControlCauseInconsistencyForm: React.FC<
  InventoryControlCauseInconsistencyFormProps
> = ({ isOpen, onClose, record, allowEdit, onSubmit, loading = false }) => {
  const [form] = useForm<RequestEditInventoryControlRegister>();

  // Resetear el formulario cuando el record cambia
  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        requestInventoryControlUUID: record.id,
        requestInventoryControlDifferenceReason: record.differenceReason,
        requestInventoryControlDifferenceSolution: record.differenceSolution,
      });
    }
  }, [record, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
      onClose();
    } catch (error) {
      console.error("Error validating form:", error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const drawerTitle = record
    ? `${
        allowEdit
          ? "Información a capturar sobre la inconsistencia:"
          : "Información inconsistente"
      } ${record.gicDate.format("DD MMM YYYY")}`
    : "";

  return (
    <Drawer
      title={drawerTitle}
      height="auto"
      placement="bottom"
      onClose={handleClose}
      open={isOpen}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
        },
      }}
      footer={
        allowEdit ? (
          <Space>
            <Button onClick={handleClose} className="back-button">
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              className="submit-button"
            >
              Guardar
            </Button>
          </Space>
        ) : (
          <Button onClick={handleClose}>Cerrar</Button>
        )
      }
    >
      {record && (
        <Form layout="vertical" form={form}>
          {allowEdit && (
            <Form.Item
              extra="* Campos obligatorios"
              label="Llena los dos campos a continuación"
            >
              <p className="instructions">
                * Esta información permanecerá editable durante el día en curso
              </p>
            </Form.Item>
          )}

          <ResponsiveCol size="xl">
            <FormItem<RequestEditInventoryControlRegister>
              hidden
              label="UUID del control de existencias"
              name="requestInventoryControlUUID"
            >
              <Input disabled />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="xl">
            <FormItem<RequestEditInventoryControlRegister>
              label="Posible causa"
              name="requestInventoryControlDifferenceReason"
              rules={[
                {
                  required: true,
                  message: "Por favor introduce la posible causa.",
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                disabled={!allowEdit || loading}
                placeholder="Describe la posible causa de la inconsistencia"
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="xl">
            <FormItem<RequestEditInventoryControlRegister>
              label="Acciones que se tomarán para su corrección y seguimiento"
              name="requestInventoryControlDifferenceSolution"
              rules={[
                {
                  required: true,
                  message: "Por favor introduce las acciones a realizar.",
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                disabled={!allowEdit || loading}
                placeholder="Describe las acciones correctivas a implementar"
              />
            </FormItem>
          </ResponsiveCol>
        </Form>
      )}
    </Drawer>
  );
};
